import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';

function formatDate(value) {
  if (!value) return 'No expiry';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function runHasEnded(run) {
  if (!run?.end_date) return false;
  const end = new Date(`${run.end_date}T23:59:59`);
  return !Number.isNaN(end.getTime()) && end < new Date();
}

function enrollmentIsCurrent(enrollment, run) {
  const status = String(enrollment?.status ?? 'active').toLowerCase();
  const expiry = enrollment?.expires_at
    ? new Date(enrollment.expires_at)
    : null;
  const expired = expiry &&
    !Number.isNaN(expiry.getTime()) &&
    expiry < new Date();

  return status === 'active' && !expired && !runHasEnded(run);
}

function EnrollmentCard({ item, getRunSlug, completed = false }) {
  const { enrollment, run, session, department } = item;

  return (
    <Card className={`border-2 ${
      completed ? 'border-tan bg-white/70' : 'border-forest-green'
    }`}>
      <div className="flex flex-col justify-between gap-5 sm:flex-row">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-amber">
            {department?.name ?? 'Learning Center'}
          </p>
          <h3 className="mt-2 text-xl font-bold text-dark-green">
            {session?.title ?? session?.name ?? 'Academy Session'}
          </h3>
          <p className="mt-1 font-medium text-forest-green">
            {run?.title ?? run?.name ?? 'Event Run'}
          </p>
          <p className="mt-3 text-sm text-muted">
            {run?.start_date ? formatDate(run.start_date) : 'Date to be announced'}
            {run?.end_date ? ` — ${formatDate(run.end_date)}` : ''}
          </p>
          <span className="mt-3 inline-flex rounded-full bg-amber/10 px-3 py-1 text-xs font-bold uppercase text-amber">
            {enrollment?.pass_type === 'allaccess'
              ? 'All-Access'
              : 'Paid Access'}
          </span>
        </div>

        <Link
          to={`/hub/${getRunSlug(run)}`}
          className="self-start sm:self-center"
        >
          <Button>
            {completed ? 'Open Archive' : 'Enter Session'}
            <SafeIcon name="ArrowRight" />
          </Button>
        </Link>
      </div>
    </Card>
  );
}

export default function Hub() {
  const { user } = useAuth();
  const location = useLocation();
  const {
    departments,
    sessions,
    runs,
    enrollments,
    loadingAcademy,
    getRunSlug
  } = useAcademy();

  const joinedEnrollments = (enrollments ?? []).map((enrollment) => {
    const run = (runs ?? []).find(
      (item) => String(item?.id) === String(enrollment?.run_id)
    );
    const session = (sessions ?? []).find(
      (item) =>
        String(item?.id) === String(
          enrollment?.session_id ?? run?.session_id
        )
    );
    const department = (departments ?? []).find(
      (item) =>
        String(item?.id) === String(
          enrollment?.department_id ?? session?.department_id
        )
    );

    return { enrollment, run, session, department };
  }).filter((item) => item.run && item.session);

  const active = joinedEnrollments.filter((item) =>
    enrollmentIsCurrent(item.enrollment, item.run)
  );
  const completed = joinedEnrollments.filter((item) =>
    !enrollmentIsCurrent(item.enrollment, item.run)
  );
  const entitlements = joinedEnrollments.filter(
    (item) => item.enrollment?.pass_type === 'allaccess'
  );

  if (loadingAcademy) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <SafeIcon name="Loader" className="animate-spin text-4xl text-amber" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream px-5 py-12">
      <div className="mx-auto max-w-5xl space-y-10">
        <header className="border-b-2 border-tan pb-6">
          <p className="text-sm font-bold uppercase tracking-widest text-amber">
            Student Hub
          </p>
          <h1 className="mt-2 text-4xl font-bold text-dark-green">
            Welcome back, {user?.name?.split(' ')[0] || 'Author'}
          </h1>
          <p className="mt-3 text-muted">
            Choose an event run to open its dashboard, content, podcast,
            letters, and lab partner experience.
          </p>
        </header>

        {location.state?.deniedRun && (
          <div className="rounded-lg border border-amber bg-amber/10 px-5 py-4 text-dark-green">
            That event run is not included in your current enrollments.
          </div>
        )}

        {joinedEnrollments.length === 0 ? (
          <Card className="py-14 text-center">
            <SafeIcon
              name="BookOpen"
              className="mx-auto mb-4 text-5xl text-amber"
            />
            <h2 className="text-2xl font-bold text-dark-green">
              No enrolled sessions yet
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-muted">
              Browse the Learning Centers to find the next event run for
              your writing journey.
            </p>
            <Link to="/centers" className="mt-7 inline-block">
              <Button>
                Browse Learning Centers
                <SafeIcon name="ArrowRight" />
              </Button>
            </Link>
          </Card>
        ) : (
          <>
            <section className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold text-dark-green">
                  Active Enrollments
                </h2>
                <p className="mt-1 text-sm text-muted">
                  Your current Academy event runs.
                </p>
              </div>

              {active.length > 0 ? (
                <div className="grid gap-5">
                  {active.map((item) => (
                    <EnrollmentCard
                      key={item.enrollment.id}
                      item={item}
                      getRunSlug={getRunSlug}
                    />
                  ))}
                </div>
              ) : (
                <Card className="text-muted">
                  You have no currently active event runs.
                </Card>
              )}
            </section>

            {completed.length > 0 && (
              <section className="space-y-4">
                <div>
                  <h2 className="text-2xl font-bold text-dark-green">
                    Completed or Past Enrollments
                  </h2>
                  <p className="mt-1 text-sm text-muted">
                    Previous sessions and available archives.
                  </p>
                </div>
                <div className="grid gap-5">
                  {completed.map((item) => (
                    <EnrollmentCard
                      key={item.enrollment.id}
                      item={item}
                      getRunSlug={getRunSlug}
                      completed
                    />
                  ))}
                </div>
              </section>
            )}

            {entitlements.length > 0 && (
              <section className="space-y-4">
                <div>
                  <h2 className="text-2xl font-bold text-dark-green">
                    All-Access Entitlements
                  </h2>
                  <p className="mt-1 text-sm text-muted">
                    Your premium benefits are attached to these event runs.
                  </p>
                </div>
                <Card className="divide-y divide-tan p-0">
                  {entitlements.map(({ enrollment, run, session }) => (
                    <div
                      key={`entitlement-${enrollment.id}`}
                      className="flex flex-col justify-between gap-3 px-6 py-5 sm:flex-row sm:items-center"
                    >
                      <div>
                        <p className="font-bold text-dark-green">
                          {session?.title ?? session?.name ?? 'Academy Session'}
                        </p>
                        <p className="text-sm text-muted">
                          {run?.title ?? run?.name ?? 'Event Run'}
                        </p>
                      </div>
                      <div className="text-sm font-semibold text-forest-green">
                        {enrollment?.expires_at
                          ? `Expires ${formatDate(enrollment.expires_at)}`
                          : 'No expiry'}
                      </div>
                    </div>
                  ))}
                </Card>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}